import { useEffect, useMemo, useRef } from 'react';
import Portal from 'react-overlays/Portal';
import { FilterSubmenu } from './FilterSubmenu';
import { CuratorSidebarLayout } from '../CuratorSidebarLayout/CuratorSidebarLayout';
import { sortBy } from 'lodash';
import { ConfiguratorFilterLayout } from '../../../Configurator/ConfiguratorFilterLayout/ConfiguratorFilterLayout';
import { useUserContext } from '../../../../contexts/UserContext';

export const FILTER = {
  MY_FAVOURITES: 'favourite',
  SHARED: 'shared',
  ARCHIVED: 'archive',
  MY_TEXTURES: 'MY_TEXTURES',
  ALL_TEXTURES: 'ALL_TEXTURES',
  MY_UPLOADS: 'MY_UPLOADS',
  ALL_UPLOADS: 'ALL_UPLOADS',
  MY_3D_MODELS: 'MY_3D_MODELS',
  PROPS: 'PROPS',
  TEXTURES: 'TEXTURES',
  UPLOADS: 'UPLOADS',
  OWNED_BY_ME: 'OWNED_BY_ME',
  ALL_USER_PROPS: 'ALL_USER_PROPS',
  LIVE_FURNISH: 'LIVE_FURNISH',
  GROUP_PROP: 'GROUP_PROP',
  USER_MODEL_UPLOADED: 'USER_MODEL_UPLOADED',
  MATTRESS_BUILDER: 'MATTRESS_BUILDER'
};

export const CuratorFilter = ({ open, onChange, filter, value, config, version, v2 = false, showSubCategories = false, showTooltip = false }) => {
  const bodyRef = useRef(null);
  const { isEditor, isGuestUser } = useUserContext();

  const isConfigurator = version === 'configurator';

  const filters = [...config];

  const apiFilters = useMemo(() => {
    if (!filter || !Object.keys(filter).length) return [];

    let result = [];

    if (Array.isArray(filter.filterlabels)) {
      let sortedFilters = sortBy(filter.filterlabels, ['order']);

      // If the user is not an editor or guest, retain only filters that are 'visuals' or not marked as product page filters.
      if (!(isEditor || isGuestUser())) {
        sortedFilters = sortedFilters?.filter(
          (a) =>
            ['visuals']?.includes(a?.label_name?.trim()?.toLowerCase()) ||
            !a?.is_product_page_filter
        );
      }

      const options = sortedFilters.map((label) => {
        return {
          key: label.id,
          title: label.label_name,
          items: filter.filteroptions
            .filter((item) => item.filter_label === label.id)
            .map((item) => ({
              label: item.name,
              value: `option_${item.filter_label}_${item.id}`,
              organizationDetailName: item.organization_detail?.name || 'imagine.io',
            })),
        };
      });

      result = [...result, ...options];
    }

    if (filter.collections && filter.collections.length > 0) {
      const collections = {
        title: 'Collection',
        key: 'collection',
        items: filter.collections.map((collection) => ({
          label: collection.name,
          value: `collection_${collection.id}`,
        })),
      };

      result = [collections, ...result];
    }

    if (filter.brands && filter.brands.length > 0) {
      const brands = {
        title: 'Brand',
        key: 'brand',
        items: filter.brands.map((brand) => ({
          label: brand.name,
          value: `brand_${brand.id}`,
        })),
      };

      result = [brands, ...result];
    }

    return result;
  }, [filter, isEditor, isGuestUser]);

  filters.push(...apiFilters);

  useEffect(() => {
    bodyRef.current = document.querySelector('body');
  }, []);

  if (!open) return null;

  const Layout = isConfigurator ? ConfiguratorFilterLayout : CuratorSidebarLayout.Filter;

  return (
    <Portal container={bodyRef}>
      <Layout>
        <FilterSubmenu
          filters={filters}
          value={value}
          showSubCategories={showSubCategories}
          onChange={onChange}
          version={isConfigurator ? '2' : '1'}
          maxVisible={isConfigurator ? 4 : undefined}
          v2={v2}
          showTooltip={showTooltip}
        />

        {!isConfigurator && <CuratorSidebarLayout.Button location="filter" />}
      </Layout>
    </Portal>
  );
};
